import type { CSSProperties } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { CardBatch } from '../../api/cards';
import { getHistoryDownloadUrl } from '../../api/cards';
import { ApiError } from '../../api/errors';
import { cardsControllerHistory } from '../../api/generated';
import i18n from '../../i18n';
import { ErrorMessage } from '../common/ErrorMessage';

function fmt(iso: string) {
  return new Date(iso).toLocaleString(i18n.language, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function without(set: Set<string>, id: string): Set<string> {
  const next = new Set(set);
  next.delete(id);
  return next;
}

function MutedLabel({ children }: { children: string }) {
  return (
    <span className="label leading-(--text-xs--line-height) text-muted-soft">
      {children}
    </span>
  );
}

/**
 * Right-hand cell of a history row: the download button, or the state that
 * explains why there is nothing to download. A failed batch never produced an
 * output file and a running one does not have it yet — offering the button in
 * either case would only 404 into the misleading "expired" label.
 */
function DownloadCell({
  batch,
  expired,
  failed,
  downloading,
  onDownload,
}: {
  batch: CardBatch;
  expired: boolean;
  failed: boolean;
  downloading: boolean;
  onDownload: () => void;
}) {
  const { t } = useTranslation();

  if (batch.status === 'failed') {
    return null;
  }
  if (batch.status === 'processing') {
    return <MutedLabel>{t('history.running')}</MutedLabel>;
  }
  if (expired) {
    return <MutedLabel>{t('history.expired')}</MutedLabel>;
  }

  return (
    <>
      <button
        onClick={onDownload}
        disabled={downloading}
        className="btn-blue px-[12px] py-[6px] text-[11px] disabled:opacity-40"
        title={t('history.download')}
      >
        {downloading ? (
          <svg className="size-4 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
            />
          </svg>
        ) : (
          <svg
            className="size-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
            />
          </svg>
        )}
        {batch.outputFormat.toUpperCase()}
      </button>
      {batch.expiresAt && (
        <span className="mono mt-0.5 block text-xs text-muted-soft">
          {t('history.expiresAt', { date: fmt(batch.expiresAt) })}
        </span>
      )}
      {failed && (
        <span className="mt-0.5 block text-xs text-magenta">
          {t('history.downloadFailed')}
        </span>
      )}
    </>
  );
}

export function BatchHistory({ isAdmin }: { isAdmin: boolean }) {
  const { t } = useTranslation();
  const [batches, setBatches] = useState<CardBatch[]>([]);
  const [loading, setLoading] = useState(true);
  // A failed load used to fall through to the empty state, which told the
  // operator they had no batch at all.
  const [loadFailed, setLoadFailed] = useState(false);
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
  const [downloading, setDownloading] = useState<Set<string>>(() => new Set());
  const [expired, setExpired] = useState<Set<string>>(() => new Set());
  const [failed, setFailed] = useState<Set<string>>(() => new Set());
  const load = useCallback(async () => {
    setLoading(true);
    setLoadFailed(false);
    try {
      const { data } = await cardsControllerHistory({ throwOnError: true });
      setBatches(data);
    } catch {
      setLoadFailed(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const toggleExpanded = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });

  const handleDownload = async (batch: CardBatch) => {
    if (expired.has(batch.id)) {
      return;
    }
    setDownloading((prev) => new Set(prev).add(batch.id));
    setFailed((prev) => without(prev, batch.id));
    try {
      const url = await getHistoryDownloadUrl(batch.id);
      const a = document.createElement('a');
      a.href = url;
      a.download = `cards-${batch.templateName}.${batch.outputFormat}`;
      a.click();
    } catch (err) {
      // Only a 404 means the file is gone: the server answers "File has
      // expired or does not exist". A network error or a 5xx is transient —
      // marking those "expired" hid the button until the page was reloaded.
      if (err instanceof ApiError && err.status === 404) {
        setExpired((prev) => new Set(prev).add(batch.id));
      } else {
        setFailed((prev) => new Set(prev).add(batch.id));
      }
    } finally {
      setDownloading((prev) => without(prev, batch.id));
    }
  };

  if (loading) {
    return <HistoryLoading />;
  }

  if (loadFailed) {
    return (
      <div className="space-y-3 py-8">
        <ErrorMessage message={t('history.loadFailed')} />
        <button type="button" onClick={() => void load()} className="btn-ghost">
          {t('common.retry')}
        </button>
      </div>
    );
  }

  if (batches.length === 0) {
    return <HistoryEmpty />;
  }

  return (
    <div className="space-y-2">
      <div className="mb-4 flex items-center justify-between">
        <p className="label">{t('history.count', { count: batches.length })}</p>
        <button
          onClick={() => {
            void load();
          }}
          className="btn-ghost px-[12px] py-[6px] text-[10px]"
        >
          {t('common.refresh')}
        </button>
      </div>

      <div className="border-t border-t-ink">
        {batches.map((b) => (
          <BatchRow
            key={b.id}
            batch={b}
            isAdmin={isAdmin}
            open={expanded.has(b.id)}
            expired={expired.has(b.id)}
            failed={failed.has(b.id)}
            downloading={downloading.has(b.id)}
            onToggle={() => toggleExpanded(b.id)}
            onDownload={() => {
              void handleDownload(b);
            }}
          />
        ))}
      </div>
    </div>
  );
}

function HistoryLoading() {
  return (
    <div className="flex justify-center py-12">
      <div className="size-6 animate-spin border-4 border-rule border-t-blue" />
    </div>
  );
}

function HistoryEmpty() {
  const { t } = useTranslation();
  return (
    <div className="py-16 text-center text-muted-soft">
      <svg
        className="mx-auto mb-3 size-10 opacity-30"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
          d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
      <p className="text-sm">{t('history.empty')}</p>
    </div>
  );
}

function BatchRow({
  batch: b,
  isAdmin,
  open,
  expired,
  failed,
  downloading,
  onToggle,
  onDownload,
}: {
  batch: CardBatch;
  isAdmin: boolean;
  open: boolean;
  expired: boolean;
  failed: boolean;
  downloading: boolean;
  onToggle: () => void;
  onDownload: () => void;
}) {
  return (
    <div className="border-b border-b-rule">
      <div className="flex items-center">
        <button
          className="flex flex-1 items-center gap-4 px-4 py-3.5 text-left transition-colors hover:bg-blue/5"
          onClick={onToggle}
        >
          {/* Date */}
          <span className="mono w-32 shrink-0 text-xs text-muted">
            {fmt(b.createdAt)}
          </span>

          {/* Template */}
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-ink">
              {b.templateName}
            </p>
            {isAdmin && b.actorName && (
              <p className="mono truncate text-xs text-muted-soft">
                {b.actorName}
                {b.actorUpn ? ` · ${b.actorUpn}` : ''}
              </p>
            )}
          </div>

          {/* Count / stats */}
          <BatchPills batch={b} />

          {/* Chevron */}
          <svg
            className={`size-4 shrink-0 text-muted-soft transition-transform ${open ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </button>

        {/* A failed batch has nothing to download or announce here —
            skip the cell entirely so the row spans the full width. */}
        {b.status !== 'failed' && (
          <div className="shrink-0 px-3 text-right">
            <DownloadCell
              batch={b}
              expired={expired}
              failed={failed}
              downloading={downloading}
              onDownload={onDownload}
            />
          </div>
        )}
      </div>

      {open && <BatchDetails batch={b} />}
    </div>
  );
}

const pillStyle = (bg: string, fg: string): CSSProperties => ({
  background: bg,
  color: fg,
  border: '1px solid currentColor',
  fontSize: '11px',
  padding: '2px 8px',
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  fontWeight: 700,
  fontFamily: "'IBM Plex Mono', monospace",
});

function BatchPills({ batch: b }: { batch: CardBatch }) {
  const { t } = useTranslation();
  return (
    <div className="flex shrink-0 items-center gap-1">
      {b.status === 'failed' && (
        <span
          style={pillStyle('rgba(255,30,247,0.10)', 'var(--magenta)')}
          title={b.failedReason ?? undefined}
        >
          {t('history.failed')}
        </span>
      )}
      {b.stats ? (
        <>
          {b.stats.success > 0 && (
            <span
              style={pillStyle('rgba(0,255,151,0.18)', '#007a47')}
              title={t('history.tooltipOk', {
                count: b.stats.success,
              })}
            >
              {b.stats.success} OK
            </span>
          )}
          {b.stats.notFound > 0 && (
            <span
              style={pillStyle('rgba(255,90,58,0.12)', 'var(--coral)')}
              title={t('history.tooltipNF', {
                count: b.stats.notFound,
              })}
            >
              {b.stats.notFound} NF
            </span>
          )}
          {b.stats.error > 0 && (
            <span
              style={pillStyle('rgba(255,30,247,0.10)', 'var(--magenta)')}
              title={t('history.tooltipErr', {
                count: b.stats.error,
              })}
            >
              {b.stats.error} ERR
            </span>
          )}
        </>
      ) : (
        <span style={pillStyle('rgba(1,58,251,0.08)', 'var(--blue)')}>
          {t('history.cards', { count: b.rowCount })}
        </span>
      )}
    </div>
  );
}

function identifierTagStyle(
  status: NonNullable<CardBatch['results']>[string] | undefined,
): CSSProperties {
  if (status === 'ok') {
    return {
      background: 'rgba(0,255,151,0.10)',
      border: '1px solid rgba(0,255,151,0.45)',
      color: '#007a47',
    };
  }
  if (status === 'notFound') {
    return {
      background: 'rgba(255,90,58,0.08)',
      border: '1px solid var(--coral)',
      color: 'var(--coral)',
    };
  }
  if (status === 'error') {
    return {
      background: 'rgba(255,30,247,0.08)',
      border: '1px solid var(--magenta)',
      color: 'var(--magenta)',
    };
  }
  return {
    background: 'var(--card)',
    border: '1px solid var(--rule)',
    color: 'var(--muted)',
  };
}

function BatchDetails({ batch: b }: { batch: CardBatch }) {
  const { t } = useTranslation();
  return (
    <div className="border-t border-t-rule bg-ink/3 px-4 py-3">
      {b.status === 'failed' && b.failedReason && (
        <p className="mono mb-2 text-xs text-magenta">
          {t('history.failedReason', { reason: b.failedReason })}
        </p>
      )}
      {b.identifiers.length === 0 ? (
        <p className="text-xs text-muted-soft italic">
          {t('history.noIdentifiers')}
        </p>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          {b.identifiers.map((id) => {
            return (
              <span
                key={id}
                className="mono px-2 py-0.5 text-xs"
                style={identifierTagStyle(b.results?.[id])}
              >
                {id}
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}
